import React, { useState } from "react";
import styled from "styled-components";
import { Snackbar } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { sendEmail } from "../../utils/emailApi";


const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #f0fdf4 0%, #e6f9ef 100%);
  position: relative;
  padding: 20px;
  overflow: hidden;
`;

const BackgroundShapes = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  background: url('/path-to-your-blobs-or-pattern.svg') no-repeat center/cover;
  opacity: 0.1;
`;

const Wrapper = styled.div`
  z-index: 1;
  width: 100%;
  max-width: 700px;
  background-color: white;
  padding: 20px;
  border-radius: 20px;
  box-shadow: 0 15px 30px rgba(0, 128, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 15px;
    border-radius: 12px;
  }
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  color: #28ac30;
  background: linear-gradient(90deg, #28ac30, #85d67f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  text-align: center;
  color: #666;
  margin-bottom: 20px;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 12px;
  background: #f8f8f8;
  transition: border 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #0a8650;
    box-shadow: 0 0 10px rgba(10, 134, 80, 0.2);
    outline: none;
    background: white;
  }
`;

const StyledPhoneInput = styled(PhoneInput)`
  width: 100%;

  .form-control {
    width: 100%;
    height: auto;
    padding: 15px 15px 15px 58px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 12px;
    background: #f8f8f8;
    transition: border 0.3s ease, box-shadow 0.3s ease;
  }

  .form-control:focus {
    border-color: #0a8650;
    box-shadow: 0 0 10px rgba(10, 134, 80, 0.2);
    outline: none;
    background: white;
  }

  .flag-dropdown {
    border: 1px solid #ccc;
    border-right: none;
    border-radius: 12px 0 0 12px;
    background: #f8f8f8;
  }

  .selected-flag {
    border-radius: 12px 0 0 12px;
  }

  .country-list {
    width: 320px;
    max-height: 240px;
    overflow-y: auto;
    border-radius: 10px;
    box-shadow: rgba(15, 23, 42, 0.18) 0 12px 28px;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 15px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 12px;
  background: #f8f8f8;
  transition: border 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #0a8650;
    box-shadow: 0 0 10px rgba(10, 134, 80, 0.2);
    outline: none;
    background: white;
  }
`;

const SubmitButton = styled.button`
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(90deg, #0a8650, #32cd32);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(50, 205, 50, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);

    try {
      await sendEmail("contact", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone,
        message: formData.message,
      });
      setOpen(true);
      setPhone("");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        message: "",
      });
    } catch (err) {
      console.error("Failed to send email:", err);
      alert("Could not send your message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Container id="contact">
      <BackgroundShapes />
      <Wrapper>
        <Title>Contact Us</Title>
        <Desc>Have any questions or feedback? We’re here to help!</Desc>
        <ContactForm onSubmit={handleSubmit}>
          <Input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="First Name"
            required
          />
          <Input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Last Name"
            required
          />
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email"
            required
          />
          <StyledPhoneInput
            country="ng"
            value={phone}
            onChange={(value) => setPhone(value)}
            countryCodeEditable={false}
            inputProps={{
              name: "phone",
              required: true,
            }}
          />
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows="5"
            placeholder="Your Message"
            required
          />
          <SubmitButton type="submit" disabled={isSending}>
            {isSending ? "Sending..." : "Submit"}
          </SubmitButton>
        </ContactForm>
        <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)} message="Message sent successfully!" />
      </Wrapper>
    </Container>
  );
};

export default Contact;
